import * as React from "react";
import * as ScrollAreaPrimitive from "@radix-ui/react-scroll-area";
import { Check, ChevronDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

interface MobileScrollSelectProps {
  value: string;
  onValueChange: (value: string) => void;
  options: string[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

export function MobileScrollSelect({
  value,
  onValueChange,
  options,
  placeholder,
  disabled,
  className,
}: MobileScrollSelectProps) {
  const [open, setOpen] = React.useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild disabled={disabled}>
        <button
          type="button"
          className={cn(
            "flex h-10 w-full items-center justify-between rounded-md border border-input bg-white px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
            className,
          )}
        >
          <span className={cn("truncate", !value && "text-muted-foreground")}>
            {value || placeholder}
          </span>
          <ChevronDown className="h-4 w-4 opacity-50 shrink-0 ml-2" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={4}
        className="p-0 bg-white border-border shadow-md rounded-md overflow-hidden"
        style={{ width: "var(--radix-popover-trigger-width)" }}
      >
        <div className="relative">
          <ScrollAreaPrimitive.Root className="relative overflow-hidden h-[260px]">
            <ScrollAreaPrimitive.Viewport className="h-full w-full">
              <div className="py-1 pr-2.5">
                {options.map((opt) => {
                  const selected = opt === value;
                  return (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => {
                        onValueChange(opt);
                        setOpen(false);
                      }}
                      className={cn(
                        "flex w-full items-center justify-between px-3 py-2 text-sm text-left transition-colors",
                        selected
                          ? "bg-primary/10 text-primary font-medium"
                          : "hover:bg-accent text-foreground",
                      )}
                    >
                      <span className="truncate">{opt}</span>
                      {selected && <Check className="h-4 w-4 shrink-0 ml-2" />}
                    </button>
                  );
                })}
              </div>
            </ScrollAreaPrimitive.Viewport>
            <ScrollAreaPrimitive.Scrollbar
              orientation="vertical"
              className="flex touch-none select-none transition-colors h-full w-2 border-l border-l-transparent p-[1px] bg-primary/5"
              forceMount
            >
              <ScrollAreaPrimitive.Thumb className="relative flex-1 rounded-full bg-primary/50 hover:bg-primary/70 transition-colors" />
            </ScrollAreaPrimitive.Scrollbar>
          </ScrollAreaPrimitive.Root>
          {/* Top fade */}
          <div className="pointer-events-none absolute top-0 left-0 right-2.5 h-4 bg-gradient-to-b from-white to-transparent" />
          {/* Bottom fade */}
          <div className="pointer-events-none absolute bottom-0 left-0 right-2.5 h-4 bg-gradient-to-t from-white to-transparent" />
        </div>
      </PopoverContent>
    </Popover>
  );
}
